import { useState, useEffect } from 'react';

import { 
  Box, 
  Drawer, 
  Grid, 
  TextField, 
  Button, 
  Divider, 
  Typography,
  IconButton,
  Chip,
  Stack
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';

// Utils
import messages from '../../static/messages';

// Styles
import { button } from '../../static/theme-styles';

// Email validation regex
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const SuppliersDrawer = (props) => {
  const { currentSupplier, setCurrentSupplier, createSupplier, updateSupplier } = props;
  const [errors, setErrors] = useState({});
  const [newEmail, setNewEmail] = useState('');
  const [emailError, setEmailError] = useState('');

  // Action functions
  const resetErrors = () => {
    setErrors({});
    setEmailError('');
    setNewEmail('');
  };

  // Handler functions
  const handleClose = () => props.onCloseDrawer(resetErrors);

  const handleChange = (e) => {
    const key = e.target.name;
    const val = e.target.value;

    setCurrentSupplier((prevState) => ({
      ...prevState,
      [key]: val,
      isEdited: true,
    }));
  };

  const handleAddEmail = () => {
    const trimmedEmail = newEmail.trim();
    
    if (!trimmedEmail) {
      setEmailError('Enter an email address');
      return;
    }
    
    if (!emailRegex.test(trimmedEmail)) {
      setEmailError('Invalid email format');
      return;
    }
    
    const currentEmails = currentSupplier.emails || [];
    if (currentEmails.includes(trimmedEmail)) {
      setEmailError('Email already added');
      return;
    }
    
    setCurrentSupplier((prevState) => ({
      ...prevState,
      emails: [...(prevState.emails || []), trimmedEmail],
      isEdited: true,
    }));
    
    setNewEmail('');
    setEmailError('');
  };

  const handleRemoveEmail = (emailToRemove) => {
    setCurrentSupplier((prevState) => ({
      ...prevState,
      emails: (prevState.emails || []).filter(e => e !== emailToRemove),
      isEdited: true,
    }));
  };

  const handleEmailKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddEmail();
    }
  };

  const handleValidate = () => {
    const newErrors = {};
    
    if (!currentSupplier.name || currentSupplier.name.trim() === '') {
      newErrors.name = true;
    }
    
    const emails = currentSupplier.emails || [];
    if (emails.length === 0) {
      newErrors.emails = true;
    }
    
    setErrors(newErrors);
    
    if (Object.keys(newErrors).length === 0) {
      if (props.isNewSupplier) {
        createSupplier();
      } else {
        updateSupplier();
      }
    }
  };

  // Hooks
  useEffect(() => {
    setErrors({});
    setNewEmail('');
    setEmailError('');
  }, [currentSupplier?.id]);

  return (
    <Drawer anchor="right" open={props.drawer} onClose={handleClose}>
      <Box sx={{ width: 400, p: 3 }}>
        {currentSupplier && (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="h5" gutterBottom>
                {props.isNewSupplier ? 'Add New Supplier' : 'Update Supplier'}
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <TextField
                required
                name="name"
                label="Supplier Name"
                value={currentSupplier.name || ''}
                fullWidth
                onChange={handleChange}
                error={errors.name}
                helperText={errors.name && messages.errors.required_field}
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                Email Addresses (orders will be sent to all)
              </Typography>
              
              {/* Current emails */}
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2, minHeight: 32 }}>
                {(currentSupplier.emails || []).map((email, idx) => (
                  <Chip
                    key={idx}
                    label={email}
                    onDelete={() => handleRemoveEmail(email)}
                    deleteIcon={<DeleteIcon />}
                    color="primary"
                    variant="outlined"
                    size="small"
                    sx={{ mb: 0.5 }}
                  />
                ))}
                {(!currentSupplier.emails || currentSupplier.emails.length === 0) && (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    No email addresses added
                  </Typography>
                )}
              </Stack>
              
              {/* Add new email */}
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  type="email"
                  label="Add Email"
                  value={newEmail}
                  onChange={(e) => {
                    setNewEmail(e.target.value);
                    setEmailError('');
                  }}
                  onKeyPress={handleEmailKeyPress}
                  error={!!emailError || errors.emails}
                  helperText={emailError || (errors.emails && 'At least one email required')}
                  fullWidth
                  size="small"
                  placeholder="email@example.com"
                />
                <IconButton 
                  onClick={handleAddEmail} 
                  color="primary"
                  sx={{ 
                    bgcolor: 'primary.main', 
                    color: 'white',
                    '&:hover': { bgcolor: 'primary.dark' },
                    height: 40,
                    width: 40
                  }}
                >
                  <AddIcon />
                </IconButton>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
            </Grid>

            <Grid item xs={12}>
              <Button
                variant="contained"
                sx={{ ...button('primary', 'secondary'), mr: 2 }}
                onClick={handleValidate}
              >
                {props.isNewSupplier ? 'Add Supplier' : 'Update Supplier'}
              </Button>

              <Button variant="outlined" onClick={handleClose}>
                Cancel
              </Button>
            </Grid>
          </Grid>
        )}
      </Box>
    </Drawer>
  );
};

export default SuppliersDrawer;
